import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import MenuItem from './MenuItem';
import { ACTIVE_COLOR } from './Styles';
import { getInitials } from './helpers';
import FAIcon from '#/common/components/FAIcon';
import {
  HR,
  OptionButton,
  Card,
  IconCircle,
  CloseButton,
  Backdrop,
  ApplicationPicker,
  MoreHeaderRowActions,
} from './Menu.styles';
import { useSwitch } from '@nuvolo/nuux-core/hooks/useSwitch';
import getMenuItems from './menuItems';
import { UserRole } from '#/common/types/userTypes';
import { RoleToRoleNameMap } from './AppMenu';
import { color } from '@nuvolo/nuux-theme/utils';
import { useJiraCollector } from '#/common/hooks/useJiraCollector';
import ApplicationPickerCard from './ApplicationPickerCard';

const PanelBackdrop = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  z-index: -1;
`;
const MoreSheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${color('secondary', 'light2')};
  padding: 16px;
  height: 250px;
  z-index: -1;
`;
const MoreHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;
const MoreHeaderTitle = styled.div`
  color: ${color('contrast', 'light')};
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 18px;
`;
const MoreGrid = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
`;
const MoreItem = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 104px;
  height: 92px;
  padding: 20px 8px;
  border-radius: 16px;
  background: ${color('custom', 'selected')};
  color: ${color('contrast', 'light')};
  cursor: pointer;
  border: ${({ active }) => (active ? `1px solid ${ACTIVE_COLOR}` : 'none')};
`;
const MoreButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 74px;
  cursor: pointer;
  color: ${color('contrast', 'light')};
`;

const MobileAvatarName = styled.div`
  line-height: 20px;
  font-size: 16px;
  font-weight: 500;
  color: ${color('custom', 'selected')};
  text-align: left !important;
`;
const MobileAvatarRole = styled.div`
  line-height: 16px;
  font-size: 12px;
  font-weight: 400;
  color: ${color('custom', 'selected')};
  opacity: 0.6;
  margin: 2px 0 12px;
  text-align: left !important;
`;

const MobileMenu: React.FC<{
  userName?: string;
  userRole?: UserRole;
  activeRoute: string;
  onLogout: () => Promise<void> | void;
}> = ({ userName, userRole, activeRoute, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuItems = getMenuItems({ role: userRole });
  const mobileItems = menuItems.filter((mi) => mi.shouldShow).filter((mi) => !mi.desktopOnly);
  const primary = mobileItems.slice(0, 3);
  const more = useMemo(() => [...mobileItems.slice(3)], [mobileItems]);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isAvatarOpen, setIsAvatarOpen] = useState(false);
  const {
    value: isApplicationPickerOpen,
    toggle: toggleApplicationPicker,
    off: closeApplicationPicker,
  } = useSwitch(false);

  const isRouteInMore = useMemo(
    () => more.some((mi) => mi.route === activeRoute),
    [more, location.pathname],
  );
  const isMoreActive = isMoreOpen || isRouteInMore;

  const { showCollector } = useJiraCollector();
  // Close sheet/avatar whenever the route changes
  useEffect(() => {
    setIsMoreOpen(false);
    setIsAvatarOpen(false);
    closeApplicationPicker();
  }, [location.pathname]);

  return (
    <>
      {/* collapsed bar */}
      <NuvoBox
        display="flex"
        alignItems="stretch"
        flexDirection="row"
        justifyContent="space-around">
        {primary.map((mi) => {
          const active = mi.route === activeRoute && !isMoreActive;
          return (
            <NuvoBox key={mi.route} flex={1}>
              <MoreButton>
                <div className="menu-item-wrapper">
                  <MenuItem {...mi} isActive={active} />
                </div>
              </MoreButton>
            </NuvoBox>
          );
        })}
        <NuvoBox flex={1}>
          <MoreButton>
            <div className="menu-item-wrapper">
              <MenuItem
                label="More"
                route={location.pathname}
                icon="ellipsis"
                isActive={isMoreActive}
                onClick={() => setIsMoreOpen((v) => !v)}
              />
            </div>
          </MoreButton>
        </NuvoBox>
      </NuvoBox>

      {/* expanded sheet */}
      {isMoreOpen && (
        <>
          {isAvatarOpen && (
            <>
              <Backdrop onClick={() => setIsAvatarOpen(false)} />
              <Card variant="mobile">
                <CloseButton onClick={() => setIsAvatarOpen(false)}>
                  <FAIcon icon="xmark" color={color('secondary', 'dark2')} />
                </CloseButton>
                <MobileAvatarName>{userName ?? ''}</MobileAvatarName>
                <MobileAvatarRole>{userRole ? RoleToRoleNameMap[userRole] : ''}</MobileAvatarRole>
                <HR />
                <OptionButton
                  size="md"
                  onClick={() => {
                    setIsAvatarOpen(false);
                    setIsMoreOpen(false);
                    showCollector();
                  }}>
                  <FAIcon icon="comment" color={color('custom', 'iconGray')} />
                  Contact Us
                </OptionButton>
                <OptionButton
                  size="md"
                  onClick={async () => {
                    setIsAvatarOpen(false);
                    try {
                      await onLogout();
                    } catch (e) {
                      console.warn('Logout failed', e);
                    }
                  }}>
                  <FAIcon icon="right-from-bracket" color={color('custom', 'iconGray')} />
                  Log Out
                </OptionButton>
              </Card>
            </>
          )}

          <ApplicationPickerCard
            opened={isApplicationPickerOpen}
            onClose={closeApplicationPicker}
            variant="mobile"
          />

          <PanelBackdrop
            onClick={() => {
              setIsMoreOpen(false);
              setIsAvatarOpen(false);
              closeApplicationPicker();
            }}
          />
          <MoreSheet role="dialog" aria-label="More Pages">
            <MoreHeaderRow>
              <MoreHeaderTitle>More Pages</MoreHeaderTitle>
              <MoreHeaderRowActions>
                <ApplicationPicker
                  active={isApplicationPickerOpen}
                  onClick={toggleApplicationPicker}>
                  <FAIcon icon="grid-2" color={color('custom', 'iconGray')} />
                </ApplicationPicker>
                {userName ? (
                  <IconCircle
                    size={36}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAvatarOpen((v) => !v);
                    }}>
                    {getInitials(userName)}
                  </IconCircle>
                ) : null}
              </MoreHeaderRowActions>
            </MoreHeaderRow>

            <MoreGrid>
              {more.map((mi) => {
                const active = mi.route === activeRoute;
                return (
                  <MoreItem
                    key={mi.route}
                    active={active}
                    // Close & then navigate (or logout)
                    onClick={async () => {
                      setIsAvatarOpen(false);
                      setIsMoreOpen(false);
                      closeApplicationPicker();
                      if (mi.route === '/logout') {
                        try {
                          await onLogout();
                        } catch (e) {
                          console.warn('Logout failed', e);
                        }
                      } else {
                        navigate(mi.route);
                      }
                    }}>
                    <div>
                      <FAIcon
                        icon={mi.icon}
                        size="xl"
                        color={active ? ACTIVE_COLOR : `${color('contrast', 'light')}`}
                      />
                    </div>
                    <NuvoText
                      type="body"
                      color={active ? ACTIVE_COLOR : `${color('contrast', 'light')}`}>
                      {mi.label}
                    </NuvoText>
                  </MoreItem>
                );
              })}
            </MoreGrid>
          </MoreSheet>
        </>
      )}
    </>
  );
};
export default React.memo(MobileMenu);
